/* ========================================================= * Material Kit 2 PRO React - v2.0.0 ========================================================= * Product Page: https://www.creative-tim.com/product/material-kit-pro-react * Copyright 2021 Creative Tim (https://www.creative-tim.com) Coded by www.creative-tim.com ========================================================= * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. */

import { useEffect, useRef, useState } from "react";
import { useMaterialUIController } from "/context";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from 'next/image';
import MDBox from "/components/MDBox";
import formatUsd from "/utils/formatUsd";
import formatTimeFriendly from "/utils/formatTimeFriendly";
import validateEmail from "/utils/validateEmail";
import usePrevious from "/utils/usePrevious";
import { robotoSlab } from "/utils/fonts";
import config from '/config';

// Images
// 20x20
// 1. Patio new
import cb2020PatioAbove from "/assets/img/patioConfigurator/cb20x20-patio-above.jpg";
import cb2020PatioBack from "/assets/img/patioConfigurator/cb20x20-patio-back.jpg";
import cb2020PatioLeft from "/assets/img/patioConfigurator/cb20x20-patio-left.jpg";
import cb2020PatioRight from "/assets/img/patioConfigurator/cb20x20-patio-right.jpg";
// 2. Patio Firepit new
import cb2020PatioFirepitAbove from "/assets/img/patioConfigurator/cb20x20-patio-firepit-above.jpg";
import cb2020PatioFirepitBack from "/assets/img/patioConfigurator/cb20x20-patio-firepit-back.jpg";
import cb2020PatioFirepitLeft from "/assets/img/patioConfigurator/cb20x20-patio-firepit-left.jpg";
import cb2020PatioFirepitRight from "/assets/img/patioConfigurator/cb20x20-patio-firepit-right.jpg";
// 3. Patio Firepit Grill new
import cb2020PatioFirepitGrillAbove from "/assets/img/patioConfigurator/cb20x20-patio-firepit-grill-above.jpg";
import cb2020PatioFirepitGrillBack from "/assets/img/patioConfigurator/cb20x20-patio-firepit-grill-back.jpg";
import cb2020PatioFirepitGrillLeft from "/assets/img/patioConfigurator/cb20x20-patio-firepit-grill-left.jpg";
import cb2020PatioFirepitGrillRight from "/assets/img/patioConfigurator/cb20x20-patio-firepit-grill-right.jpg";
// 4. Patio Firepit Wall new
import cb2020PatioFirepitWallAbove from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-above.jpg";
import cb2020PatioFirepitWallBack from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-back.jpg";
import cb2020PatioFirepitWallLeft from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-left.jpg";
import cb2020PatioFirepitWallRight from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-right.jpg";
// 5. Patio Firepit Wall Grill new
import cb2020PatioFirepitWallGrillAbove from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-grill-above.jpg";
import cb2020PatioFirepitWallGrillBack from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-grill-back.jpg";
import cb2020PatioFirepitWallGrillLeft from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-grill-left.jpg"
import cb2020PatioFirepitWallGrillRight from "/assets/img/patioConfigurator/cb20x20-patio-firepit-wall-grill-right.jpg"
// 6. Patio Grill new
import cb2020PatioGrillAbove from "/assets/img/patioConfigurator/cb20x20-patio-grill-above.jpg";
import cb2020PatioGrillBack from "/assets/img/patioConfigurator/cb20x20-patio-grill-back.jpg";
import cb2020PatioGrillLeft from "/assets/img/patioConfigurator/cb20x20-patio-grill-left.jpg";
import cb2020PatioGrillRight from "/assets/img/patioConfigurator/cb20x20-patio-grill-right.jpg";
// 7. Patio Wall new
import cb2020PatioWallAbove from "/assets/img/patioConfigurator/cb20x20-patio-wall-above.jpg";
import cb2020PatioWallBack from "/assets/img/patioConfigurator/cb20x20-patio-wall-back.jpg";
import cb2020PatioWallLeft from "/assets/img/patioConfigurator/cb20x20-patio-wall-left.jpg";
import cb2020PatioWallRight from "/assets/img/patioConfigurator/cb20x20-patio-wall-right.jpg";
// 8. Patio Wall Grill new
import cb2020PatioWallGrillAbove from "/assets/img/patioConfigurator/cb20x20-patio-wall-grill-above.jpg";
import cb2020PatioWallGrillBack from "/assets/img/patioConfigurator/cb20x20-patio-wall-grill-back.jpg";
import cb2020PatioWallGrillLeft from "/assets/img/patioConfigurator/cb20x20-patio-wall-grill-left.jpg";
import cb2020PatioWallGrillRight from "/assets/img/patioConfigurator/cb20x20-patio-wall-grill-right.jpg";

// 20x25
// 1. Patio new
import cb2025PatioAbove from "/assets/img/patioConfigurator/cb20x25-patio-above.jpg";
import cb2025PatioBack from "/assets/img/patioConfigurator/cb20x25-patio-back.jpg";
import cb2025PatioLeft from "/assets/img/patioConfigurator/cb20x25-patio-left.jpg";
import cb2025PatioRight from "/assets/img/patioConfigurator/cb20x25-patio-right.jpg";
// 2. Patio Firepit new
import cb2025PatioFirepitAbove from "/assets/img/patioConfigurator/cb20x25-patio-firepit-above.jpg"; // need a regular height one
import cb2025PatioFirepitBack from "/assets/img/patioConfigurator/cb20x25-patio-firepit-back.jpg";
import cb2025PatioFirepitLeft from "/assets/img/patioConfigurator/cb20x25-patio-firepit-left.jpg";
import cb2025PatioFirepitRight from "/assets/img/patioConfigurator/cb20x25-patio-firepit-right.jpg";
// 3. Patio Firepit Grill new
import cb2025PatioFirepitGrillAbove from "/assets/img/patioConfigurator/cb20x25-patio-firepit-grill-above.jpg";
import cb2025PatioFirepitGrillBack from "/assets/img/patioConfigurator/cb20x25-patio-firepit-grill-back.jpg";
import cb2025PatioFirepitGrillLeft from "/assets/img/patioConfigurator/cb20x25-patio-firepit-grill-left.jpg";
import cb2025PatioFirepitGrillRight from "/assets/img/patioConfigurator/cb20x25-patio-firepit-grill-right.jpg";
// 4. Patio Firepit Wall new
import cb2025PatioFirepitWallAbove from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-above.jpg";
import cb2025PatioFirepitWallBack from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-back.jpg";
import cb2025PatioFirepitWallLeft from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-left.jpg";
import cb2025PatioFirepitWallRight from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-right.jpg";
// 5. Patio Firepit Wall Grill new
import cb2025PatioFirepitWallGrillAbove from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-grill-above.jpg";
import cb2025PatioFirepitWallGrillBack from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-grill-back.jpg";
import cb2025PatioFirepitWallGrillLeft from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-grill-left.jpg";
import cb2025PatioFirepitWallGrillRight from "/assets/img/patioConfigurator/cb20x25-patio-firepit-wall-grill-right.jpg";
// 6. Patio Grill new
import cb2025PatioGrillAbove from "/assets/img/patioConfigurator/cb20x25-patio-grill-above.jpg";
import cb2025PatioGrillBack from "/assets/img/patioConfigurator/cb20x25-patio-grill-back.jpg";
import cb2025PatioGrillLeft from "/assets/img/patioConfigurator/cb20x25-patio-grill-left.jpg";
import cb2025PatioGrillRight from "/assets/img/patioConfigurator/cb20x25-patio-grill-right.jpg";
// 7. Patio Wall new
import cb2025PatioWallAbove from "/assets/img/patioConfigurator/cb20x25-patio-wall-above.jpg";
import cb2025PatioWallBack from "/assets/img/patioConfigurator/cb20x25-patio-wall-back.jpg";
import cb2025PatioWallLeft from "/assets/img/patioConfigurator/cb20x25-patio-wall-left.jpg";
import cb2025PatioWallRight from "/assets/img/patioConfigurator/cb20x25-patio-wall-right.jpg";
// 8. Patio Wall Grill new
import cb2025PatioWallGrillAbove from "/assets/img/patioConfigurator/cb20x25-patio-wall-grill-above.jpg";
import cb2025PatioWallGrillBack from "/assets/img/patioConfigurator/cb20x25-patio-wall-grill-back.jpg";
import cb2025PatioWallGrillLeft from "/assets/img/patioConfigurator/cb20x25-patio-wall-grill-left.jpg";
import cb2025PatioWallGrillRight from "/assets/img/patioConfigurator/cb20x25-patio-wall-grill-right.jpg";

// 20x30
// 1. Patio new
import cb2030PatioAbove from "/assets/img/patioConfigurator/cb20x30-patio-above.jpg";
import cb2030PatioBack from "/assets/img/patioConfigurator/cb20x30-patio-back.jpg";
import cb2030PatioLeft from "/assets/img/patioConfigurator/cb20x30-patio-left.jpg";
import cb2030PatioRight from "/assets/img/patioConfigurator/cb20x30-patio-right.jpg";
// 2. Patio Firepit new
import cb2030PatioFirepitAbove from "/assets/img/patioConfigurator/cb20x30-patio-firepit-above.jpg";
import cb2030PatioFirepitBack from "/assets/img/patioConfigurator/cb20x30-patio-firepit-back.jpg";
import cb2030PatioFirepitLeft from "/assets/img/patioConfigurator/cb20x30-patio-firepit-left.jpg";
import cb2030PatioFirepitRight from "/assets/img/patioConfigurator/cb20x30-patio-firepit-right.jpg";
// 3. Patio Firepit Grill new
import cb2030PatioFirepitGrillAbove from "/assets/img/patioConfigurator/cb20x30-patio-firepit-grill-above.jpg";
import cb2030PatioFirepitGrillBack from "/assets/img/patioConfigurator/cb20x30-patio-firepit-grill-back.jpg";
import cb2030PatioFirepitGrillLeft from "/assets/img/patioConfigurator/cb20x30-patio-firepit-grill-left.jpg";
import cb2030PatioFirepitGrillRight from "/assets/img/patioConfigurator/cb20x30-patio-firepit-grill-right.jpg";
// 4. Patio Firepit Wall new
import cb2030PatioFirepitWallAbove from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-above.jpg";
import cb2030PatioFirepitWallBack from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-back.jpg";
import cb2030PatioFirepitWallLeft from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-left.jpg";
import cb2030PatioFirepitWallRight from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-right.jpg";
// 5. Patio Firepit Wall Grill new
import cb2030PatioFirepitWallGrillAbove from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-grill-above.jpg";
import cb2030PatioFirepitWallGrillBack from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-grill-back.jpg";
import cb2030PatioFirepitWallGrillLeft from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-grill-left.jpg";
import cb2030PatioFirepitWallGrillRight from "/assets/img/patioConfigurator/cb20x30-patio-firepit-wall-grill-right.jpg";
// 6. Patio Grill new
import cb2030PatioGrillAbove from "/assets/img/patioConfigurator/cb20x30-patio-grill-above.jpg";
import cb2030PatioGrillBack from "/assets/img/patioConfigurator/cb20x30-patio-grill-back.jpg";
import cb2030PatioGrillLeft from "/assets/img/patioConfigurator/cb20x30-patio-grill-left.jpg";
import cb2030PatioGrillRight from "/assets/img/patioConfigurator/cb20x30-patio-grill-right.jpg";
// 7. Patio Wall new
import cb2030PatioWallAbove from "/assets/img/patioConfigurator/cb20x30-patio-wall-above.jpg";
import cb2030PatioWallBack from "/assets/img/patioConfigurator/cb20x30-patio-wall-back.jpg";
import cb2030PatioWallLeft from "/assets/img/patioConfigurator/cb20x30-patio-wall-left.jpg";
import cb2030PatioWallRight from "/assets/img/patioConfigurator/cb20x30-patio-wall-right.jpg";
// 8. Patio Wall Grill new
import cb2030PatioWallGrillAbove from "/assets/img/patioConfigurator/cb20x30-patio-wall-grill-above.jpg";
import cb2030PatioWallGrillBack from "/assets/img/patioConfigurator/cb20x30-patio-wall-grill-back.jpg";
import cb2030PatioWallGrillLeft from "/assets/img/patioConfigurator/cb20x30-patio-wall-grill-left.jpg";
import cb2030PatioWallGrillRight from "/assets/img/patioConfigurator/cb20x30-patio-wall-grill-right.jpg";

// 20x40
// 1. Patio new
import cb2540PatioAbove from "/assets/img/patioConfigurator/cb20x40-patio-above.jpg";
import cb2540PatioBack from "/assets/img/patioConfigurator/cb20x40-patio-back.jpg";
import cb2540PatioLeft from "/assets/img/patioConfigurator/cb20x40-patio-left.jpg";
import cb2540PatioRight from "/assets/img/patioConfigurator/cb20x40-patio-right.jpg";
// 2. Patio Firepit new
import cb2540PatioFirepitAbove from "/assets/img/patioConfigurator/cb20x40-patio-firepit-above.jpg";
import cb2540PatioFirepitBack from "/assets/img/patioConfigurator/cb20x40-patio-firepit-back.jpg";
import cb2540PatioFirepitLeft from "/assets/img/patioConfigurator/cb20x40-patio-firepit-left.jpg";
import cb2540PatioFirepitRight from "/assets/img/patioConfigurator/cb20x40-patio-firepit-right.jpg";
// 3. Patio Firepit Grill new
import cb2540PatioFirepitGrillAbove from "/assets/img/patioConfigurator/cb20x40-patio-firepit-grill-above.jpg";
import cb2540PatioFirepitGrillBack from "/assets/img/patioConfigurator/cb20x40-patio-firepit-grill-back.jpg";
import cb2540PatioFirepitGrillLeft from "/assets/img/patioConfigurator/cb20x40-patio-firepit-grill-left.jpg";
import cb2540PatioFirepitGrillRight from "/assets/img/patioConfigurator/cb20x40-patio-firepit-grill-right.jpg";
// 4. Patio Firepit Wall new
import cb2540PatioFirepitWallAbove from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-above.jpg";
import cb2540PatioFirepitWallBack from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-back.jpg";
import cb2540PatioFirepitWallLeft from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-left.jpg";
import cb2540PatioFirepitWallRight from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-right.jpg";
// 5. Patio Firepit Wall Grill new
import cb2540PatioFirepitWallGrillAbove from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-grill-above.jpg";
import cb2540PatioFirepitWallGrillBack from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-grill-back.jpg";
import cb2540PatioFirepitWallGrillLeft from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-grill-left.jpg";
import cb2540PatioFirepitWallGrillRight from "/assets/img/patioConfigurator/cb20x40-patio-firepit-wall-grill-right.jpg";
// 6. Patio Grill new
import cb2540PatioGrillAbove from "/assets/img/patioConfigurator/cb20x40-patio-grill-above.jpg";
import cb2540PatioGrillBack from "/assets/img/patioConfigurator/cb20x40-patio-grill-back.jpg";
import cb2540PatioGrillLeft from "/assets/img/patioConfigurator/cb20x40-patio-grill-left.jpg";
import cb2540PatioGrillRight from "/assets/img/patioConfigurator/cb20x40-patio-grill-right.jpg";
// 7. Patio Wall new
import cb2540PatioWallAbove from "/assets/img/patioConfigurator/cb20x40-patio-wall-above.jpg";
import cb2540PatioWallBack from "/assets/img/patioConfigurator/cb20x40-patio-wall-back.jpg";
import cb2540PatioWallLeft from "/assets/img/patioConfigurator/cb20x40-patio-wall-left.jpg";
import cb2540PatioWallRight from "/assets/img/patioConfigurator/cb20x40-patio-wall-right.jpg";
// 8. Patio Wall Grill new
import cb2540PatioWallGrillAbove from "/assets/img/patioConfigurator/cb20x40-patio-wall-grill-above.jpg";
import cb2540PatioWallGrillBack from "/assets/img/patioConfigurator/cb20x40-patio-wall-grill-back.jpg";
import cb2540PatioWallGrillLeft from "/assets/img/patioConfigurator/cb20x40-patio-wall-grill-left.jpg";
import cb2540PatioWallGrillRight from "/assets/img/patioConfigurator/cb20x40-patio-wall-grill-right.jpg";

// MUI
const Card = dynamic(() => import("@mui/material/Card"), { loading: () => null });
const Grid = dynamic(() => import("@mui/material/Grid"), { loading: () => null });
const Container = dynamic(() => import("@mui/material/Container"), { loading: () => null });
const Dialog = dynamic(() => import("@mui/material/Dialog"), { loading: () => null });
const DialogActions = dynamic(() => import("@mui/material/DialogActions"), { loading: () => null });
const DialogContent = dynamic(() => import("@mui/material/DialogContent"), { loading: () => null });
const DialogTitle = dynamic(() => import("@mui/material/DialogTitle"), { loading: () => null });
const Divider = dynamic(() => import("@mui/material/Divider"), { loading: () => null });
const FormControl = dynamic(() => import("@mui/material/FormControl"), { loading: () => null });
const FormGroup = dynamic(() => import("@mui/material/FormGroup"), { loading: () => null });
const FormLabel = dynamic(() => import("@mui/material/FormLabel"), { loading: () => null });
const FormControlLabel = dynamic(() => import("@mui/material/FormControlLabel"), { loading: () => null });
const InputAdornment = dynamic(() => import("@mui/material/InputAdornment"), { loading: () => null });
const Radio = dynamic(() => import('@mui/material/Radio'), { loading: () => null });
const RadioGroup = dynamic(() => import('@mui/material/RadioGroup'), { loading: () => null });
const Switch = dynamic(() => import('@mui/material/Switch'), { loading: () => null });
const Tooltip = dynamic(() => import("@mui/material/Tooltip"), { loading: () => null });

// MUI icons
const DoneRounded = dynamic(() => import('@mui/icons-material/DoneRounded'), { loading: () => null });
const ErrorRounded = dynamic(() => import('@mui/icons-material/ErrorRounded'), { loading: () => null });
const Mail = dynamic(() => import('@mui/icons-material/Mail'), { loading: () => null });

// Examples
const PageLayout = dynamic(() => import("/examples/PageLayout"), { loading: () => null });
const BodyCard = dynamic(() => import("/examples/BodyCard"), { loading: () => null });

// dynamic parts
const FindUsOn = dynamic(() => import('/examples/FindUsOn'), { loading: () => null });

// parts
const MDButton = dynamic(() => import('/components/MDButton'), { loading: () => null });
const MDInput = dynamic(() => import('/components/MDInput'), { loading: () => null });
const MDSnackbar = dynamic(() => import('/components/MDSnackbar'), { loading: () => null });
const MDTypography = dynamic(() => import('/components/MDTypography'), { loading: () => null });
const GoogleAnalytics = dynamic(() => import('/examples/GoogleAnalytics'), { loading: () => null });

// prices
const patioPrice20x20 = 640000;
const patioPrice20x25 = 800000;
const patioPrice20x30 = 960000;
const patioPrice20x40 = 1280000;
const firepitPrice = 135000;
const grillPrice = 970000;
const wallPrice = 135000;
const step1Price = 179900;
const step2Price = 310000;
const step3Price = 385000;
const step4Price = 489900;

function PatioConfigurator() {
  const [controller] = useMaterialUIController();
  const headerRef = useRef(null);
  const [size, setSize] = useState("20x25");
  const [total, setTotal] = useState(patioPrice20x25);
  const [view, setView] = useState("above");
  const [wall, setWall] = useState(false);
  const [firepit, setFirepit] = useState(false)
  const [grill, setGrill] = useState(false);
  const [steps, setSteps] = useState(0);
  const [snackbar, setSnackbar] = useState({});
  const [openSendOrderDialog, setOpenSendOrderDialog] = useState(false);
  // send order 
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  // const fade
  const [opacity, setOpacity] = useState(1);
  // image
  const [image, setImage] = useState(cb2020PatioAbove);
  const prevImage = usePrevious(image);

  useEffect(() => {
    setTimeout(() => {
      setOpacity(0);
    }, 3000);
  }, []);

  useEffect(() => {
    let parallax = null;
    const start = async () => {
      if (headerRef.current !== null) {
        await import("rellax").then(u => parallax = new u.default(headerRef.current, { speed: -6 })
        )
      }
    };
    start();
    return () => {
      if (parallax !== undefined && parallax !== null) parallax.destroy()
    }
  }, [headerRef.current]);

  // subtotal calculator
  useEffect(() => {
    let isSubscribed = true;
    let newSubtotal = 0;
    if (size === "20x20") newSubtotal = patioPrice20x20;
    else if (size === "20x25") newSubtotal = patioPrice20x25;
    else if (size === "20x30") newSubtotal = patioPrice20x30;
    else if (size === "20x40") newSubtotal = patioPrice20x40;
    // accessories
    if (wall === true) {
      newSubtotal += wallPrice;
    }
    if (firepit === true) {
      newSubtotal += firepitPrice;
    }
    if (grill === true) {
      newSubtotal += grillPrice;
    }
    if (steps === 1) {
      newSubtotal += step1Price;
    }
    if (steps === 2) {
      newSubtotal += step2Price;
    }
    if (steps === 3) {
      newSubtotal += step3Price;
    }
    if (steps === 4) {
      newSubtotal += step4Price;
    }
    if (isSubscribed === true) {
      setTotal(newSubtotal);
    }
    return () => isSubscribed = false;
  }, [size, wall, firepit, grill, steps]);

  function closeDialog() {
    setName("");
    setEmail("");
    setPhone("");
    setOpenSendOrderDialog(false);
  }

  useEffect(() => {
    let selectedImage;
    let isSubscribed = true;
    if (size === "20x20") {
      if (view === "above") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2020PatioAbove;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2020PatioFirepitWallGrillAbove;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2020PatioFirepitAbove;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2020PatioFirepitWallAbove;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2020PatioFirepitGrillAbove;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2020PatioWallAbove;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2020PatioWallGrillAbove;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2020PatioGrillAbove;
      }
      else if (view === "back") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2020PatioBack;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2020PatioFirepitWallGrillBack;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2020PatioFirepitBack;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2020PatioFirepitWallBack;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2020PatioFirepitGrillBack;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2020PatioWallBack;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2020PatioWallGrillBack;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2020PatioGrillBack;
      }
      else if (view === "left") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2020PatioLeft;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2020PatioFirepitWallGrillLeft;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2020PatioFirepitLeft;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2020PatioFirepitWallLeft;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2020PatioFirepitGrillLeft;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2020PatioWallLeft;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2020PatioWallGrillLeft;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2020PatioGrillLeft;
      }
      else if (view === "right") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2020PatioRight;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2020PatioFirepitWallGrillRight;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2020PatioFirepitRight;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2020PatioFirepitWallRight;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2020PatioFirepitGrillRight;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2020PatioWallRight;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2020PatioWallGrillRight;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2020PatioGrillRight;
      }
    }
    else if (size === "20x25") {
      if (view === "above") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2025PatioAbove;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2025PatioFirepitWallGrillAbove;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2025PatioFirepitAbove;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2025PatioFirepitWallAbove;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2025PatioFirepitGrillAbove;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2025PatioWallAbove;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2025PatioWallGrillAbove;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2025PatioGrillAbove;
      }
      else if (view === "back") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2025PatioBack;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2025PatioFirepitWallGrillBack;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2025PatioFirepitBack;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2025PatioFirepitWallBack;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2025PatioFirepitGrillBack;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2025PatioWallBack;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2025PatioWallGrillBack;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2025PatioGrillBack;
      }
      else if (view === "left") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2025PatioLeft;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2025PatioFirepitWallGrillLeft;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2025PatioFirepitLeft;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2025PatioFirepitWallLeft;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2025PatioFirepitGrillLeft;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2025PatioWallLeft;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2025PatioWallGrillLeft;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2025PatioGrillLeft;
      }
      else if (view === "right") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2025PatioRight;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2025PatioFirepitWallGrillRight;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2025PatioFirepitRight;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2025PatioFirepitWallRight;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2025PatioFirepitGrillRight;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2025PatioWallRight;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2025PatioWallGrillRight;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2025PatioGrillRight;
      }
    }
    else if (size === "20x30") {
      if (view === "above") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2030PatioAbove;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2030PatioFirepitWallGrillAbove;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2030PatioFirepitAbove;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2030PatioFirepitWallAbove;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2030PatioFirepitGrillAbove;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2030PatioWallAbove;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2030PatioWallGrillAbove;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2030PatioGrillAbove;
      }
      else if (view === "back") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2030PatioBack;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2030PatioFirepitWallGrillBack;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2030PatioFirepitBack;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2030PatioFirepitWallBack;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2030PatioFirepitGrillBack;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2030PatioWallBack;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2030PatioWallGrillBack;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2030PatioGrillBack;
      }
      else if (view === "left") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2030PatioLeft;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2030PatioFirepitWallGrillLeft;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2030PatioFirepitLeft;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2030PatioFirepitWallLeft;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2030PatioFirepitGrillLeft;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2030PatioWallLeft;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2030PatioWallGrillLeft;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2030PatioGrillLeft;
      }
      else if (view === "right") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2030PatioRight;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2030PatioFirepitWallGrillRight;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2030PatioFirepitRight;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2030PatioFirepitWallRight;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2030PatioFirepitGrillRight;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2030PatioWallRight;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2030PatioWallGrillRight;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2030PatioGrillRight;
      }
    }
    else if (size === "20x40") {
      if (view === "above") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2540PatioAbove;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2540PatioFirepitWallGrillAbove;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2540PatioFirepitAbove;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2540PatioFirepitWallAbove;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2540PatioFirepitGrillAbove;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2540PatioWallAbove;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2540PatioWallGrillAbove;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2540PatioGrillAbove;
      }
      else if (view === "back") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2540PatioBack;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2540PatioFirepitWallGrillBack;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2540PatioFirepitBack;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2540PatioFirepitWallBack;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2540PatioFirepitGrillBack;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2540PatioWallBack;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2540PatioWallGrillBack;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2540PatioGrillBack;
      }
      else if (view === "left") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2540PatioLeft;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2540PatioFirepitWallGrillLeft;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2540PatioFirepitLeft;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2540PatioFirepitWallLeft;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2540PatioFirepitGrillLeft;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2540PatioWallLeft;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2540PatioWallGrillLeft;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2540PatioGrillLeft;
      }
      else if (view === "right") {
        if (firepit === false && wall === false && grill === false) selectedImage = cb2540PatioRight;
        else if (firepit === true && wall === true && grill === true) selectedImage = cb2540PatioFirepitWallGrillRight;
        else if (firepit === true && wall === false && grill === false) selectedImage = cb2540PatioFirepitRight;
        else if (firepit === true && wall === true && grill === false) selectedImage = cb2540PatioFirepitWallRight;
        else if (firepit === true && wall === false && grill === true) selectedImage = cb2540PatioFirepitGrillRight;
        else if (firepit === false && wall === true && grill === false) selectedImage = cb2540PatioWallRight;
        else if (firepit === false && wall === true && grill === true) selectedImage = cb2540PatioWallGrillRight;
        else if (firepit === false && wall === false && grill === true) selectedImage = cb2540PatioGrillRight;
      }
    }

    if (isSubscribed) setImage(selectedImage);
    return () => isSubscribed = false;
  }, [size, view, firepit, wall, grill]);

  return (
    <>
      <Head>
        <title key="title">Patio Builder by Autumn Leaf</title>
        <meta name="description" content="Configure a patio online right now. Like what you designed? We build it for you. Serving Long Island, New York." />
        <meta property="og:site_name" content="Autumn Leaf Landscape" />
        <meta property="og:url" content={`${config.frontURL}/patio-configurator`} />
        <meta property="og:title" content="Patio Configurator." />
        <meta property="og:description" content="Configure a patio online right now. Like what you designed? We build it for you. Serving Long Island, New York." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={`${config.frontURL}/images/social/patio-configurator-facebook.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="photo" />
        <meta name="twitter:title" content="Patio Configurator." />
        <meta name="twitter:description" content="Configure a patio online right now. Like what you designed? We build it for you. Serving Long Island, New York." />
        <meta name="twitter:url" content={`${config.frontURL}/patio-configurator`} />
        <meta name="twitter:image" content={`${config.frontURL}/images/social/patio-configurator-twitter.jpg`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "Product",
              "name": "Cambridge Ledgestone Patio",
              "image": `${config.frontURL}/cb20x20-patio-above.jpg`,
              "description": "Configure your patio",
              "brand": {
                "@type": "Brand",
                "name": "Autumn Leaf Landscape",
                "logo": `${config.frontURL}/logo.png`,
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "5",
                "reviewCount": "46"
              },
              "review": [
                {
                  "@type": "Review",
                  "author": [{
                    "@type": "Person",
                    "name": "Long Island Home Owner",
                  }],
                  "datePublished": "2011-04-01",
                  "reviewBody": "We love it",
                  "name": "Excellent Value, Great Quality",
                  "reviewRating": {
                    "@type": "Rating",
                    "bestRating": "5",
                    "ratingValue": "5",
                    "worstRating": "1"
                  }
                }
              ],
              "offers": [
                {
                  "@type": "Offer",
                  "name": "Cambridge 20x20 Ledgestone Patio",
                  "priceCurrency": "USD",
                  "price": patioPrice20x20 / 100,
                  "priceValidUntil": `${new Date().getFullYear()}-12-31`,
                  "availability": "https://schema.org/InStock",
                  "url": `${config.frontURL}/patio-configurator`,
                  "shippingDetails": {
                    "deliveryTime": {
                      "@type": "ShippingDeliveryTime",
                      "handlingTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      },
                      "transitTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      }
                    },
                    "shippingRate": {
                      "@type": "MonetaryAmount",
                      "value": 0.00,
                      "currency": "USD"
                    },
                    "shippingDestination": {
                      "@type": "DefinedRegion",
                      "addressCountry": "US",
                      "addressRegion": "NY",
                      "postalCodeRange": {
                        "@type": "PostalCodeRangeSpecification",
                        "postalCodeBegin": "11001",
                        "postalCodeEnd": "11969"
                      }
                    },
                  },
                  "hasMerchantReturnPolicy": {
                    "@type": "MerchantReturnPolicy",
                    "applicableCountry": "US",
                    "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
                    "merchantReturnDays": 0,
                  },
                  "addOn": [
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": firepitPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Seat wall",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": wallPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": grillPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "1 Step",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step1Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "2 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step2Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "3 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step3Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "4 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step4Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                  ],
                },
                {
                  "@type": "Offer",
                  "name": "Cambridge 20x25 Ledgestone Patio",
                  "priceCurrency": "USD",
                  "priceValidUntil": `${new Date().getFullYear()}-12-31`,
                  "price": patioPrice20x25 / 100,
                  "availability": "https://schema.org/InStock",
                  "url": `${config.frontURL}/patio-configurator`,
                  "shippingDetails": {
                    "deliveryTime": {
                      "@type": "ShippingDeliveryTime",
                      "handlingTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      },
                      "transitTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      }
                    },
                    "shippingRate": {
                      "@type": "MonetaryAmount",
                      "value": 0.00,
                      "currency": "USD"
                    },
                    "shippingDestination": {
                      "@type": "DefinedRegion",
                      "addressCountry": "US",
                      "addressRegion": "NY",
                      "postalCodeRange": {
                        "@type": "PostalCodeRangeSpecification",
                        "postalCodeBegin": "11001",
                        "postalCodeEnd": "11969"
                      }
                    },
                  },
                  "hasMerchantReturnPolicy": {
                    "@type": "MerchantReturnPolicy",
                    "applicableCountry": "US",
                    "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
                    "merchantReturnDays": 0,
                  },
                  "addOn": [
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": firepitPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Seat wall",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": wallPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": grillPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "1 Step",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step1Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "2 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step2Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "3 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step3Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "4 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step4Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                  ],
                },
                {
                  "@type": "Offer",
                  "name": "Cambridge 20x30 Ledgestone Patio",
                  "priceCurrency": "USD",
                  "price": patioPrice20x30 / 100,
                  "priceValidUntil": `${new Date().getFullYear()}-12-31`,
                  "availability": "https://schema.org/InStock",
                  "shippingDetails": {
                    "deliveryTime": {
                      "@type": "ShippingDeliveryTime",
                      "handlingTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      },
                      "transitTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      }
                    },
                    "shippingRate": {
                      "@type": "MonetaryAmount",
                      "value": 0.00,
                      "currency": "USD"
                    },
                    "shippingDestination": {
                      "@type": "DefinedRegion",
                      "addressCountry": "US",
                      "addressRegion": "NY",
                      "postalCodeRange": {
                        "@type": "PostalCodeRangeSpecification",
                        "postalCodeBegin": "11001",
                        "postalCodeEnd": "11969"
                      }
                    },
                  },
                  "hasMerchantReturnPolicy": {
                    "@type": "MerchantReturnPolicy",
                    "applicableCountry": "US",
                    "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
                    "merchantReturnDays": 0,
                  },
                  "addOn": [
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": firepitPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Seat wall",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": wallPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": grillPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "1 Step",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step1Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "2 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step2Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "3 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step3Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "4 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step4Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                  ],
                },
                {
                  "@type": "Offer",
                  "name": "Cambridge 20x40 Ledgestone Patio",
                  "priceCurrency": "USD",
                  "price": patioPrice20x40 / 100,
                  "priceValidUntil": `${new Date().getFullYear()}-12-31`,
                  "availability": "https://schema.org/InStock",
                  "url": `${config.frontURL}/patio-configurator`,
                  "shippingDetails": {
                    "deliveryTime": {
                      "@type": "ShippingDeliveryTime",
                      "handlingTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      },
                      "transitTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 14,
                        "unitCode": "DAY"
                      }
                    },
                    "shippingRate": {
                      "@type": "MonetaryAmount",
                      "value": 0.00,
                      "currency": "USD"
                    },
                    "shippingDestination": {
                      "@type": "DefinedRegion",
                      "addressCountry": "US",
                      "addressRegion": "NY",
                      "postalCodeRange": {
                        "@type": "PostalCodeRangeSpecification",
                        "postalCodeBegin": "11001",
                        "postalCodeEnd": "11969"
                      }
                    },
                  },
                  "hasMerchantReturnPolicy": {
                    "@type": "MerchantReturnPolicy",
                    "applicableCountry": "US",
                    "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted",
                    "merchantReturnDays": 0,
                  },
                  "addOn": [
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": firepitPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Seat wall",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": wallPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "Built-in Grill",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": grillPrice / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "1 Step",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step1Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "2 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step2Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "3 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step3Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                    {
                      "@type": "Offer",
                      "name": "4 Steps",
                      "url": `${config.frontURL}/patio-configurator`,
                      "price": step4Price / 100,
                      "priceCurrency": "USD",
                      "availability": "https://schema.org/InStock",
                      "url": `${config.frontURL}/patio-configurator`,
                    },
                  ],
                },
              ],
            }
            )
          }}
        />
      </Head>
      <GoogleAnalytics />
      <PageLayout transparent light>
        <MDBox
          ref={headerRef}
          width="100%"
          sx={{
            display: "grid",
            placeItems: "top",
            position: "relative",
          }}
        >
          <MDBox sx={{ display: "block", position: "relative" }}>
            <Image
              src={image || prevImage}
              alt={`${size} patio from ${view} ${firepit && "with firepit"} ${wall && "with wall"} ${grill && "with grill"} `}
              width={960}
              height={540}
              sizes="100vw"
              style={{ width: '100%', height: 'auto' }}
            />
          </MDBox>
          <Container sx={{
            position: "absolute", top: controller.mobileView ? "10vh" : "15vw", left: 0, right: 0, display: "flex", alignItems: "center", flexDirection: "column",
            opacity: opacity,
            transition: "opacity 1.5s",
          }}>
            <Grid
              container
              item
              xs={12}
              lg={8}
              justifyContent="center"
              flexDirection="column"
              sx={{ mx: "auto", textAlign: "center", alignItems: "center" }}
            >
              <MDTypography
                variant="h1"
                color="white"
                mb={0}
                pb={0}
                sx={({ breakpoints, typography: { size } }) => ({
                  zIndex: 1,
                  display: "block",
                  minHeight: 80,
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                })}
                className={robotoSlab.className}
              >
                Configure your patio
              </MDTypography>
              <MDBox mt={controller.mobileView ? -3 : 0}>
                <FindUsOn />
              </MDBox>
            </Grid>
          </Container>
        </MDBox>
        <BodyCard mt={controller.mobileView ? -3 : -18}>
          <Container>
            <MDTypography variant="h5" className={robotoSlab.className}>Cambridge {size} Ledgestone Patio</MDTypography>
            <br />
            <Grid container>
              <Grid item xs={4} md={3} lg={2}>
                <MDBox>
                  <FormControl>
                    <FormLabel id="demo-radio-buttons-group-label" color="dark">
                      <MDTypography variant="h6">Size</MDTypography>
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      defaultValue="above"
                      name="radio-buttons-group"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                    >
                      <FormControlLabel value="20x20" control={<Radio />} label="20x20" />
                      <FormControlLabel value="20x25" control={<Radio />} label="20x25" />
                      <FormControlLabel value="20x30" control={<Radio />} label="20x30" />
                      <FormControlLabel value="20x40" control={<Radio />} label="20x40" />
                    </RadioGroup>
                  </FormControl>
                  <br />
                  <br />
                </MDBox>
              </Grid>
              <Grid item xs={8} md={5} lg={3}>
                <FormControl>
                  <FormLabel id="demo-radio-buttons-group-label" color="dark">
                    <MDTypography variant="h6">Features</MDTypography>
                  </FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      label={`Seat Wall (+${formatUsd(wallPrice / 100)})`}
                      control={<Switch
                        checked={wall}
                        onChange={(e) => setWall(!wall)} />}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      label={`Firepit (+${formatUsd(firepitPrice / 100)})`}
                      control={<Switch checked={firepit} onChange={(e) => setFirepit(!firepit)} />}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      label={`Built-in Grill (+${formatUsd(grillPrice / 100)})`}
                      control={<Switch checked={grill} onChange={(e) => setGrill(!grill)} />}
                    />
                  </FormGroup>
                </FormControl>
                <br />
                <br />
              </Grid>
              <Grid item xs={4} md={4} lg={2}>
                <FormControl>
                  <FormLabel id="demo-radio-buttons-group-label" color="dark">
                    <MDTypography variant="h6">Perspective</MDTypography>
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue="above"
                    name="radio-buttons-group"
                    onChange={(e) => setView(e.target.value)}
                  >
                    <FormControlLabel value="above" control={<Radio />} label="Above" />
                    <FormControlLabel value="back" control={<Radio />} label="Back" />
                    <FormControlLabel value="left" control={<Radio />} label="Left" />
                    <FormControlLabel value="right" control={<Radio />} label="Right" />
                  </RadioGroup>
                </FormControl>
                <br />
                <br />
              </Grid>
              <Grid item xs={8} md={4} lg={2}>
                <FormControl>
                  <FormLabel id="demo-radio-buttons-group-label" color="dark">
                    <MDTypography variant="h6">Steps (1 shown)</MDTypography>
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue={0}
                    name="radio-buttons-group"
                    onChange={(e) => setSteps(Number(e.target.value))}
                  >
                    <FormControlLabel
                      value={0}
                      control={<Radio />}
                      label="0" />
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label={`1 (+${formatUsd(step1Price / 100)})`}
                    />
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label={`2 (+${formatUsd(step2Price / 100)})`}
                    />
                    <FormControlLabel
                      value={3}
                      control={<Radio />}
                      label={`3 (+${formatUsd(step3Price / 100)})`}
                    />
                    <FormControlLabel
                      value={4}
                      control={<Radio />}
                      label={`4 (+${formatUsd(step4Price / 100)})`}
                    />
                  </RadioGroup>
                </FormControl>
                <br />
                <br />
              </Grid>
              <Grid item xs={12} md={12} lg={3}>
                <MDBox>
                  <Card >
                    <MDBox p={2}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} textAlign="left">
                          <MDTypography variant="h6">
                            Your Patio as Configured
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6} textAlign="left">
                          <MDTypography variant="body2" fontWeight="regular">
                            {size} Patio
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <MDTypography variant="body2">{formatUsd(
                            size === "20x20" ? patioPrice20x20 / 100
                              : size === "20x25" ? patioPrice20x25 / 100
                                : size === "20x30" ? patioPrice20x30 / 100
                                  : size === "20x40" ? patioPrice20x40 / 100
                                    : 0)}</MDTypography>
                        </Grid>
                        {wall && (
                          <>
                            <Grid item xs={6} textAlign="left">
                              <MDTypography variant="body2" fontWeight="regular">
                                Seat Wall
                              </MDTypography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                              <MDTypography variant="body2">{formatUsd(wallPrice / 100)}</MDTypography>
                            </Grid>
                          </>
                        )}
                        {firepit && (
                          <>
                            <Grid item xs={6} textAlign="left">
                              <MDTypography variant="body2" fontWeight="regular">
                                Fire pit
                              </MDTypography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                              <MDTypography variant="body2">{formatUsd(firepitPrice / 100)}</MDTypography>
                            </Grid>
                          </>
                        )}
                        {grill && (
                          <>
                            <Grid item xs={6} textAlign="left">
                              <MDTypography variant="body2" fontWeight="regular">
                                Built-in Grill
                              </MDTypography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                              <MDTypography variant="body2">{formatUsd(grillPrice / 100)}</MDTypography>
                            </Grid>
                          </>
                        )}
                        {steps !== 0 && (
                          <>
                            <Grid item xs={6} textAlign="left">
                              <MDTypography variant="body2" fontWeight="regular">
                                {steps} Step{steps !== 1 && "s"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                              <MDTypography variant="body2">{formatUsd(
                                (steps === 1 ? step1Price
                                  : steps === 2 ? step2Price
                                    : steps === 3 ? step3Price
                                      : steps === 4 ? step4Price
                                        : 0
                                )
                                / 100)}</MDTypography>
                            </Grid>
                          </>
                        )}
                        <br />
                        <Divider />
                        {/* 
                  <Grid item xs={6} textAlign="left">
                    <MDTypography variant="body2" fontWeight="regular">
                      Sub Total
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <MDTypography variant="body2">{formatUsd(subTotal / 100)}</MDTypography>
                  </Grid>
                  */}
                        {/* 
                  <Grid item xs={6} textAlign="left">
                    <MDTypography variant="body2" fontWeight="regular">
                      Other
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <MDTypography variant="body2">0.00</MDTypography>
                  </Grid>
                  */}
                        <br />
                        <br />
                        <Grid item xs={8} textAlign="left">
                          <MDTypography variant="body1" fontWeight="regular">
                            Total
                          </MDTypography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          <MDTypography variant="body2">{formatUsd(total / 100)}</MDTypography>
                        </Grid>
                        <br />
                        <br />
                        <Grid item xs={8} textAlign="left">
                          <MDTypography variant="body2" fontWeight="regular">
                            Deposit required to order
                          </MDTypography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          <MDTypography variant="body2">{formatUsd(total / 100 / 5)}</MDTypography>
                        </Grid>
                        <Grid item xs={8} textAlign="left">
                          <MDTypography variant="body2" fontWeight="regular">
                            Balance after deposit
                          </MDTypography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                          <MDTypography variant="body2">{formatUsd((total - total / 5) / 100)}</MDTypography>
                        </Grid>
                      </Grid>
                      <br />
                      <Divider />
                      <MDBox mb={1}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          fullWidth
                          startIcon={<Mail />}
                          onClick={async (e) => {
                            setSnackbar({});
                            setOpenSendOrderDialog(true);
                          }}
                        >
                          Email Configuration
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>

            <br />
            <br />
            <br />
            <br />
            <br />
          </Container>
        </BodyCard>
      </PageLayout>

      <Dialog

        fullWidth
        maxWidth="xs"
        open={openSendOrderDialog === true}
        onClose={() => closeDialog()}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { backgroundColor: "transparent", overflowY: "visible" } }}
      >
        <Card>
          <DialogTitle id="alert-dialog-title">
            Email Configuration
          </DialogTitle>
          <DialogContent>
            <MDBox mt={1}>
              <MDInput
                type="text"
                label="Your name"
                required
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </MDBox>
            <br />
            <MDBox>
              <MDInput
                type="email"
                label="Your email"
                required
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  endAdornment: (
                    email !== "" && !validateEmail(email) ? (
                      <Tooltip title="Not a valid email">
                        <span>
                          <InputAdornment position="end">
                            <ErrorRounded color="error" />
                          </InputAdornment>
                        </span>
                      </Tooltip>
                    ) : email !== "" && validateEmail(email) ? (
                      <Tooltip title="Valid email">
                        <span>
                          <InputAdornment position="end">
                            <DoneRounded color="success" />
                          </InputAdornment>
                        </span>
                      </Tooltip>
                    ) : null
                  ),
                }}
              />
            </MDBox>
            <br />
            <MDBox>
              <MDInput
                type="tel"
                label="Your phone"
                required
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </MDBox>
          </DialogContent >
          <DialogActions>
            {snackbar.color !== "success" && (
              <>
                <MDButton onClick={() => closeDialog()}>Cancel</MDButton>
                <MDButton
                  variant="contained"
                  color="dark"
                  onClick={async (e) => {
                    e.preventDefault();
                    const resultUser = await fetch("/api/send-patio-config-to-user", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        name: name,
                        email: email,
                        phone: phone,
                        wall: wall,
                        firepit: firepit,
                        grill: grill,
                        steps: steps,
                        total: total,
                        patioPrice: size === "20x20" ? patioPrice20x20
                          : size === "20x25" ? patioPrice20x25
                            : size === "20x30" ? patioPrice20x30
                              : size === "20x40" ? patioPrice20x40
                                : 0,
                        firepitPrice: firepitPrice,
                        grillPrice: grillPrice,
                        wallPrice: wallPrice,
                        stepsPrice: steps === 1 ? step1Price
                          : steps === 2 ? step2Price
                            : steps === 3 ? step3Price
                              : steps === 4 ? step4Price
                                : 0,

                      }),
                    })
                      .then((response) => response.json())
                      .then((response) => response.message)
                      .catch(() => "error");
                    const result = await fetch("/api/send-patio-config-to-company", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        name: name,
                        email: email,
                        phone: phone,
                        wall: wall,
                        firepit: firepit,
                        grill: grill,
                        steps: steps,
                        total: total,
                        patioPrice: size === "20x20" ? patioPrice20x20 / 100
                          : size === "20x25" ? patioPrice20x25 / 100
                            : size === "20x30" ? patioPrice20x30 / 100
                              : size === "20x40" ? patioPrice20x40 / 100
                                : 0,
                        firepitPrice: firepitPrice,
                        grillPrice: grillPrice,
                        wallPrice: wallPrice,
                        stepsPrice: steps === 1 ? step1Price
                          : steps === 2 ? step2Price
                            : steps === 3 ? step3Price
                              : steps === 4 ? step4Price
                                : 0,
                        resultUser: resultUser,

                      }),
                    })
                      .then((response) => response.json())
                      .then((response) => response.message)
                      .catch(() => "error");
                    if (result !== "success") {
                      setSnackbar({ title: "Error!", content: "Send didn't go through. Try again. ", color: "info" })
                    } else {
                      setSnackbar({ title: "Success!", content: "Patio config sent successfully!", color: "success" })
                    }
                  }}>Send</MDButton>
              </>
            )}
            {snackbar.color === "success" && (
              <MDButton variant="contained" color="dark" onClick={() => closeDialog()}>Close</MDButton>
            )}

          </DialogActions>
        </Card>
      </Dialog >
      {Object.keys(snackbar).length !== 0 && (
        <MDSnackbar
          title={snackbar.title || ""}
          content={snackbar.content || ""}
          dateTime={formatTimeFriendly()}
          color={snackbar.color || "dark"}
          open={true}
          close={() => setSnackbar({})}
        />
      )
      }
    </>
  );
}

export default PatioConfigurator;
